import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { 
  TrendingUp, 
  Users, 
  FolderKanban, 
  Crown,
  CheckCircle2,
  XCircle
} from "lucide-react";
import { useTranslation } from 'react-i18next';

function UpgradeModal({ open, onClose, limitType, currentUsage, limit, planName }) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleUpgrade = () => {
    navigate('/settings?tab=billing');
    onClose();
  };

  const getLimitIcon = () => {
    if (limitType === 'users') return <Users className="h-12 w-12 text-orange-500" />;
    if (limitType === 'projects') return <FolderKanban className="h-12 w-12 text-orange-500" />;
    return <TrendingUp className="h-12 w-12 text-orange-500" />;
  };

  const getLimitTitle = () => {
    if (limitType === 'users') return t('upgrade.userLimitReached');
    if (limitType === 'projects') return t('upgrade.projectLimitReached');
    return t('upgrade.limitReached');
  };

  const getLimitDescription = () => {
    if (limitType === 'users') {
      return t('upgrade.userLimitDescription', { current: currentUsage, limit });
    }
    if (limitType === 'projects') {
      return t('upgrade.projectLimitDescription', { current: currentUsage, limit });
    }
    return t('upgrade.limitDescription');
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <div className="flex items-center justify-center mb-4">
            {getLimitIcon()}
          </div>
          <DialogTitle className="text-center text-2xl">{getLimitTitle()}</DialogTitle>
          <DialogDescription className="text-center text-lg">
            {getLimitDescription()}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 mt-6">
          {/* Current Plan Info */}
          <Card className="bg-gray-50 border-2 border-gray-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">{t('upgrade.currentPlan')}</p>
                  <p className="text-xl font-bold">{planName || 'Free'}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-600">{t('upgrade.currentUsage')}</p>
                  <p className="text-xl font-bold text-orange-600">
                    {currentUsage} / {limit}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Upgrade Benefits */}
          <div>
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Crown className="h-5 w-5 text-yellow-500" />
              {t('upgrade.professionalBenefits')}
            </h3>
            <div className="grid md:grid-cols-2 gap-3">
              <div className="flex items-start gap-2">
                <CheckCircle2 className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                <span className="text-sm">{t('upgrade.unlimitedUsers')}</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle2 className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                <span className="text-sm">{t('upgrade.unlimitedProjects')}</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle2 className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                <span className="text-sm">{t('upgrade.allViewModes')}</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle2 className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                <span className="text-sm">{t('upgrade.advancedReporting')}</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle2 className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                <span className="text-sm">{t('upgrade.activityLog')}</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle2 className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                <span className="text-sm">{t('upgrade.prioritySupport')}</span>
              </div>
            </div>
          </div>

          {/* Free Plan Limitations */}
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
            <h4 className="font-semibold mb-2 text-orange-900">{t('upgrade.freeLimitations')}</h4>
            <div className="space-y-1">
              <div className="flex items-start gap-2 text-sm text-orange-800">
                <XCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span>{t('landing.feature5Users')}</span>
              </div>
              <div className="flex items-start gap-2 text-sm text-orange-800">
                <XCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span>{t('landing.feature3Projects')}</span>
              </div>
              <div className="flex items-start gap-2 text-sm text-orange-800">
                <XCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span>{t('landing.featureBasicTasks')}</span>
              </div>
              <div className="flex items-start gap-2 text-sm text-orange-800">
                <XCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span>{t('landing.featureKanban')}</span>
              </div>
              <div className="flex items-start gap-2 text-sm text-orange-800">
                <XCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span>{t('landing.featureEmailNotifications')}</span>
              </div>
              <div className="flex items-start gap-2 text-sm text-orange-800">
                <XCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span>{t('landing.featureCommunitySupport')}</span>
              </div>
            </div>
          </div>

          {/* Pricing */}
          <Card className="border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-blue-50">
            <CardContent className="p-6 text-center">
              <p className="text-sm text-gray-600 mb-2">{t('upgrade.professionalPlan')}</p>
              <p className="text-4xl font-bold text-purple-600 mb-1">₺299</p>
              <p className="text-sm text-gray-500 mb-4">/ {t('billing.month')}</p>
              <Button 
                onClick={handleUpgrade} 
                size="lg" 
                className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
              >
                <Crown className="h-5 w-5 mr-2" />
                {t('upgrade.upgradeNow')}
              </Button>
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <div className="flex gap-3 justify-end">
            <Button variant="outline" onClick={onClose}>
              {t('upgrade.maybeLater')}
            </Button>
            <Button onClick={handleUpgrade}>
              {t('upgrade.viewPlans')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default UpgradeModal;
